import { useState, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import i18n from '../../i18n/i18n';

// Components
import DeleteSlotBadge from '../DeleteSlotBadge';

// MUI imports
import { Box, Button, Typography } from '@mui/material';

// Helpers
import { useSessionStore } from '../../store/SessionStore';
import { SlotDayData, SlotPeriod, SlotData } from '../../types/slotData';
import { deleteSlot } from '../../helpers/api/callSlot';

interface SlotPeriodPickerProps {
    slot: SlotDayData;
    editMode: boolean;
    onPeriodOfDayChange: (period: SlotPeriod.FIRST_PERIOD | SlotPeriod.SECOND_PERIOD) => void;
}

const SlotPeriodPicker = ({ slot, onPeriodOfDayChange, editMode }: SlotPeriodPickerProps) => {
    const theme = useTheme();
    const { reloadSession } = useSessionStore();
    const contentBgColor = theme.palette.mode === 'light' ? '#FFFFFF' : '#3F4C59';
    const contentBoxShadow = theme.palette.mode === 'light' ? '0px 3px 7px 0px rgba(0,0,0,0.15)' : 'none';

    const [selectedPeriod, setSelectedPeriod] = useState<SlotPeriod | null>(null);

    const formatPeriod = (start?: string, end?: string) => `${start ? format(new Date(start), 'HH:mm') : 'N/A'} - ${end ? format(new Date(end), 'HH:mm') : 'N/A'}`;

    useEffect(() => {
        if (slot.firstPeriod || slot.secondPeriod) {
            handleSelect(slot.firstPeriod ? SlotPeriod.FIRST_PERIOD : SlotPeriod.SECOND_PERIOD);
        }
    }, [slot]);

    const handleSelect = (period: SlotPeriod.FIRST_PERIOD | SlotPeriod.SECOND_PERIOD) => {
        setSelectedPeriod(period);
        onPeriodOfDayChange(period);
    };

    const handleDeleteSlot = async (period: SlotData) => {
        try {
            const response = await deleteSlot(period.id);

            if (response.code === 400) {
                toast.error(i18n.t('UpdateSession.errors.deleteSlot'));
            }

            toast.success(i18n.t('UpdateSession.success.deleteSlot'));
            reloadSession();
        } catch (error) {
            console.error('Error deleting slot:', error);
            toast.error(i18n.t('UpdateSession.errors.deleteSlot'));
        }
    };

    const periods = [
        {
            type: SlotPeriod.FIRST_PERIOD,
            start: slot.firstPeriod?.startDate,
            end: slot.firstPeriod?.endDate,
            badgeWidth: '97%',
            onClose: () => slot.firstPeriod && handleDeleteSlot(slot.firstPeriod)
        },
        {
            type: SlotPeriod.SECOND_PERIOD,
            start: slot.secondPeriod?.startDate,
            end: slot.secondPeriod?.endDate,
            badgeWidth: '93%',
            onClose: () => slot.secondPeriod && handleDeleteSlot(slot.secondPeriod)
        }
    ];

    const renderPeriodButton = (period: (typeof periods)[number]) => {
        const isSelected = selectedPeriod === period.type;
        const timeLabel = formatPeriod(period.start, period.end);

        const button = (
            <Button
                fullWidth
                onClick={() => handleSelect(period.type)}
                sx={{
                    color: isSelected ? theme.palette.background.default : theme.palette.primary.main,
                    fontWeight: '700',
                    borderRadius: '10px',
                    padding: '8px 16px',
                    width: '100%'
                }}
            >
                {timeLabel}
            </Button>
        );

        return (
            <Box key={period.type} sx={{ width: '50%', zIndex: 1, position: 'relative' }}>
                {isSelected && editMode ? (
                    <DeleteSlotBadge
                        customSx={{ width: period.badgeWidth }}
                        onCloseClick={(e) => {
                            e.stopPropagation();
                            period.onClose();
                        }}
                    >
                        {button}
                    </DeleteSlotBadge>
                ) : (
                    button
                )}
            </Box>
        );
    };

    if (!slot.firstPeriod || !slot.secondPeriod) {
        const singleSlot = slot.firstPeriod || slot.secondPeriod!;
        const label = formatPeriod(singleSlot.startDate, singleSlot.endDate);

        const content = (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: contentBgColor,
                    borderRadius: '10px',
                    width: '100%',
                    margin: '0 auto 0.6em auto',
                    boxShadow: contentBoxShadow,
                    padding: '9px 0'
                }}
            >
                <Typography sx={{ fontSize: '0.9em', fontWeight: '700' }} color={theme.palette.text.custom}>
                    {label}
                </Typography>
            </Box>
        );

        return editMode ? (
            <DeleteSlotBadge
                customSx={{ width: '100%' }}
                onCloseClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSlot(singleSlot);
                }}
            >
                {content}
            </DeleteSlotBadge>
        ) : (
            content
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                backgroundColor: contentBgColor,
                borderRadius: '10px',
                width: '100%',
                margin: '0 auto 0.6em auto',
                boxShadow: contentBoxShadow,
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '7px',
                    left: selectedPeriod === SlotPeriod.FIRST_PERIOD ? '6px' : '50%',
                    width: '46.5%',
                    height: 'calc(100% - 13px)',
                    backgroundColor: theme.palette.text.custom,
                    borderRadius: '8px',
                    transition: 'left 0.3s ease-in-out'
                }}
            />
            {periods.map(renderPeriodButton)}
        </Box>
    );
};

export default SlotPeriodPicker;
